import React, { useState, useRef } from "react";
import { X, Send, Loader2, AlertTriangle, Lightbulb, Handshake, MessageSquare, Paperclip, FileText } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";

const CATEGORIES = [
  { value: "probleme", label: "Remonter un problème", icon: AlertTriangle },
  { value: "evolution", label: "Proposer une évolution / amélioration", icon: Lightbulb },
  { value: "partenariat", label: "Proposer un partenariat", icon: Handshake },
  { value: "divers", label: "Divers", icon: MessageSquare },
];

export const ContactAdminModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [category, setCategory] = useState("probleme");
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  if (!open) return null;

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error("Fichier trop lourd (max 15 Mo)"); return; }
    setFile(f);
  };

  const reset = () => { setMessage(""); setFile(null); setCategory("probleme"); };

  const submit = async () => {
    if (!message.trim()) { toast.error("Merci de saisir votre message"); return; }
    setBusy(true);
    try {
      const res = await api.post("/conversations/contact-admin", { category, message });
      if (file && res?.data?.conversation_id) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("caption", "");
        try { await api.post(`/conversations/${res.data.conversation_id}/attachments`, fd); }
        catch { toast.error("Message envoyé mais l'ajout du fichier a échoué"); }
      }
      toast.success("Message envoyé à l'administrateur");
      reset();
      onClose();
      navigate("/app/messagerie");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="contact-admin-modal">
      <div className="bg-white rounded-3xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display text-xl font-semibold text-brand-greenDeep">Contacter l'administrateur</h3>
          <button onClick={onClose} data-testid="contact-admin-close"><X className="w-5 h-5 text-brand-green/50" /></button>
        </div>
        <p className="text-xs text-brand-green/55 mb-4">Choisissez l'objet de votre demande et écrivez votre message. Il apparaîtra dans votre messagerie et l'administrateur pourra vous répondre.</p>

        <label className="block text-xs font-semibold text-brand-green/70 mb-2">Objet de la demande</label>
        <div className="grid sm:grid-cols-2 gap-2 mb-4">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            const active = category === c.value;
            return (
              <button key={c.value} onClick={() => setCategory(c.value)} data-testid={`contact-cat-${c.value}`}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium text-left transition-colors ${active ? "bg-brand-greenDeep text-white border-brand-greenDeep" : "border-brand-soft text-brand-greenDeep hover:bg-brand-softer"}`}>
                <Icon className="w-4 h-4 shrink-0" /> <span className="leading-tight">{c.label}</span>
              </button>
            );
          })}
        </div>

        <label className="block text-xs font-semibold text-brand-green/70 mb-2">Votre message</label>
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5}
          placeholder="Décrivez votre demande…" data-testid="contact-admin-message"
          className="w-full px-3 py-2.5 rounded-xl border border-brand-soft outline-none focus:border-brand-lime text-sm text-brand-greenDeep resize-none" />

        <input ref={fileRef} type="file" className="hidden" onChange={pickFile}
          accept=".jpg,.jpeg,.png,.gif,.webp,.pdf" data-testid="contact-admin-file-input" />
        <div className="mt-3 flex items-center gap-2">
          <button onClick={() => fileRef.current?.click()} data-testid="contact-admin-attach-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-brand-soft text-brand-greenDeep text-sm font-semibold hover:bg-brand-softer transition-colors">
            <Paperclip className="w-4 h-4" /> Joindre une capture / un PDF
          </button>
          {file && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-softer text-brand-greenDeep text-xs" data-testid="contact-admin-file-chip">
              <FileText className="w-3.5 h-3.5" /> <span className="truncate max-w-[160px]">{file.name}</span>
              <button onClick={() => setFile(null)} data-testid="contact-admin-file-remove"><X className="w-3.5 h-3.5" /></button>
            </span>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2.5 rounded-full border border-brand-soft text-brand-greenDeep text-sm font-semibold hover:bg-brand-softer" data-testid="contact-admin-cancel">Annuler</button>
          <button onClick={submit} disabled={busy} className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold disabled:opacity-60" data-testid="contact-admin-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Envoyer
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactAdminModal;
